import { Link } from "react-router-dom";
import { DoiBadge } from "../../components/PublishedMethodology";

/*
 * Blog article: "Built Here First: The Ideas TRT Plus Brought to TRT"
 * Rendered inside BlogPost.jsx via <article className="legal-prose">.
 */
export default function ArticleBody() {
    return (
        <>
            <h2>Quick answer</h2>
            <p>
                A lot of the ideas now common in TRT and peptide apps started
                here: the dose calculators, the rotation planners, the
                half-life charts... but those are the easy pieces. The
                whole engine framework and the actual protocol optimization,
                the part that matters, is still only in TRT Plus.
            </p>

            <h2>One developer, one obsession</h2>
            <p>
                Bringing any single one of these features below to market would be
                impressive. Bringing all of them, in one app, is a different
                thing entirely.
            </p>
            <p>
                Some of it has since shown up in other apps, but only ever
                the easy pieces: the tracker, the reminders, the rotation
                planner. The engine underneath, the models and the protocol
                optimization, hasn't been matched, because{" "}
                <Link to="/blog/tracking-a-bad-protocol-is-still-a-bad-protocol">
                    logging a protocol is the easy part
                </Link>
                ; getting it right is the part nobody else has built.
            </p>

            <h2>The developer's journey to build TRT Plus</h2>
            <p>
                Appreciating the importance of precision started two decades
                before at Crown Scientific, calibrating CAD specifications
                for pharmaceuticals. An error here becomes
                an incorrect dose at scale, with the medical and liability
                risk that carries. Getting a dose exactly right was the
                whole point then, and is one of the foundation features of
                TRT Plus today.
            </p>
            <p>
                The two decades since were spent in financial markets as a
                lead developer, repeatedly appointed to the highest-profile
                projects, building the data pipelines and automation that
                route and reconcile billions in trades, where an error is
                measured in millions.
            </p>
            <p>
                Two very different worlds, overlapping in precision and
                provability. Some of the best innovations are cross-overs,
                concepts carried in from a parallel field. A few of these
                echoed ideas shaped this build directly:
            </p>
            <ul>
                <li>
                    A compound's concentration over time is a stack of
                    exponentially decaying doses settling into a steady
                    state superposition. Once it's there, PTF% measures how
                    much testosterone swings, the way volatility measures
                    the swing of a market.
                </li>
                <li>
                    A hematocrit ceiling that flexes with your
                    cardiovascular health is a risk limit that flexes with
                    the fundamentals: strong markers earn more room, the
                    way strong company ratings earn more exposure.
                </li>
                <li>
                    The Testimator™ scaling your predicted testosterone up
                    or down as you change a protocol is, mathematically,
                    not far from how a gearing ratio scales exposure in a
                    leveraged fund.
                </li>
                <li>
                    It's also why the bloodwork analytics engine runs
                    deterministically first, with AI used only to
                    interpret the finished result,{" "}
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        never to improvise the numbers
                    </Link>
                    .
                </li>
            </ul>

            <h2>A timeline of firsts</h2>
            <p>
                Every date below is a public App Store and Google Play
                release.
            </p>

            <h3>2023</h3>
            <ul>
                <li>
                    <strong>
                        Free testosterone with bound-fraction visualization
                        (April 2023).
                    </strong>{" "}
                    The first app to show free testosterone against its
                    bound fractions as a readable pie chart, instead of a
                    single number with no context.
                </li>
                <li>
                    <strong>
                        Closed-loop dose calculator with syringe diagram
                        (May 2023).
                    </strong>{" "}
                    The first closed-loop calculator: enter a dose to get
                    the volume, or a volume to get the dose, recalculating
                    live as frequency or concentration changes, shown on a
                    visual syringe.
                </li>
                <li>
                    <strong>The Testimator™ (May 2023).</strong> The first
                    to model your individual response to testosterone
                    and predict your levels on protocols you haven't run
                    yet, anchored to your own bloodwork, with reference
                    ranges that update as your results evolve. A competitor
                    later built an entire standalone app around this one
                    idea, but assumed blood is always drawn at peak, so the
                    whole model was flawed from the start.
                </li>
                <li>
                    <strong>
                        Peak-to-Trough Fluctuation, PTF% (May 2023).
                    </strong>{" "}
                    The first to put a real number on how far your levels
                    actually swing between peak and trough. It comes from
                    the superposition of your overlapping doses, not the
                    sawtooth most apps draw, and it's the value that tracks
                    stability and side effects. It's also a genuinely hard
                    calculation, raw dog coded in 2023, before AI.
                </li>
            </ul>

            <h3>2024</h3>
            <ul>
                <li>
                    <strong>
                        The Biomarker Dashboard (September 2024).
                    </strong>{" "}
                    Your bloodwork scorecard on a single screenshot instead
                    of a long cryptic list: an overall health score and the
                    ratios that matter, red, amber, and green pulling your
                    eye to what needs attention, high and low flags that
                    read at a glance, trimmed to only the markers that move
                    the needle on TRT, with a tap into each marker's
                    personalized range and knowledge base. Built to be
                    taken in like a trading dashboard, not scrolled like a
                    lab printout.
                    <DoiBadge slug="overall-score" />
                </li>
                <li>
                    <strong>
                        Enhanced reference ranges (September 2024).
                    </strong>{" "}
                    The first app to score your bloodwork against
                    calibrated ranges built for men on TRT and enhanced
                    protocols, derived
                    from de-identified real-world clinic data rather than
                    the general population. General "optimal range"
                    products existed for the healthy population, but TRT- and
                    enhanced-specific ranges had lived only in clinics and
                    coaching circles until now.
                </li>
                <li>
                    <strong>
                        Forward-looking injection rotation planner
                        (November 2024).
                    </strong>{" "}
                    The first to plan your injection sites forward across
                    your protocol, rather than just logging where you've
                    already been.
                </li>
            </ul>

            <h3>2025</h3>
            <ul>
                <li>
                    <strong>
                        The Zakharov free-T method (October 2025).
                    </strong>{" "}
                    The first app to bring the Zakharov method into the
                    spotlight as the superior free-T model for enhanced and
                    high-testosterone profiles, where SHBG saturates and its
                    allosteric binding model is what holds up. Mazer,
                    Södergård and the original Vermeulen sit alongside it.
                </li>
                <li>
                    <strong>
                        Route-specific pharmacokinetic models (October
                        2025).
                    </strong>{" "}
                    Orals, aqueous suspensions, subcutaneous peptides, and
                    oil esters each modeled by how they are actually
                    absorbed.
                </li>
                <li>
                    <strong>The Clogging Quartet (October 2025).</strong>{" "}
                    Coined here: a framework for the four atherosclerosis
                    markers most relevant to TRT, ApoB, CRP, HbA1c, and
                    blood pressure, whose combined risk is greater than
                    the sum of the parts.
                </li>
                <li>
                    <strong>
                        Compound stacking in the syringe (October 2025).
                    </strong>{" "}
                    Combine multiple compounds into a single injection and
                    verify the volumes visually, the way people running
                    several enhancement compounds actually load one
                    syringe to cut down the number of injections.
                </li>
            </ul>

            <h3>2026</h3>
            <ul>
                <li>
                    <strong>
                        The A:E ratio, Total Androgenic Load (March 2026).
                    </strong>{" "}
                    The first androgen-to-estrogen model to fold in DHT's
                    substantially greater receptor affinity and its
                    non-linear amplification at the tissue level, using an
                    asymmetric model, for a truer read of hormonal balance
                    than a flat T:E ratio.
                    <DoiBadge slug="androgen-load" />
                </li>
                <li>
                    <strong>
                        Personalized testosterone reference range with a
                        dynamic hematocrit ceiling (April 2026).
                    </strong>{" "}
                    The first to extend your tolerance for a marker from
                    your own cardiovascular and metabolic profile, rather
                    than reading it off a population bell curve.
                </li>
                <li>
                    <strong>
                        The Cardio-Metabolic Cascade (May 2026).
                    </strong>{" "}
                    The framework underneath that personalization: your
                    cardiovascular and metabolic markers feed two connected
                    cascades, one that extends your testosterone range, one
                    that gates your hematocrit ceiling. Blood Pressure,
                    lipids, Body Fat, CRP and HbA1c are weighed together,
                    the way a clinician interprets them.
                    <DoiBadge slug="cascade" />
                </li>
                <li>
                    <strong>
                        Transdermal saturation model and cream-to-injectable
                        converter (May 2026).
                    </strong>{" "}
                    The first to model cream absorption with a saturation
                    function site by site and express it as a weekly
                    injectable-equivalent, so a topical protocol is
                    directly comparable to an injectable one, upper
                    saturation limits included.
                    <DoiBadge slug="cream" />
                </li>
                <li>
                    <strong>Dose Titration (July 2026).</strong>{" "}
                    The first titration planner that calculates the steps
                    for you, in the real graduation increments your syringe
                    can actually draw, and stays flexible on step cadence,
                    duration, and start date for the ramp or taper. It
                    even recalibrates if you adjust the protocol mid-plan.
                </li>
                <li>
                    <strong>Frequency Tuning (July 2026).</strong> The
                    first app to read your exact dose, frequency, and
                    testosterone formulation and flag when your
                    peak-to-trough swing is larger than it needs to be,
                    the driver behind peak-related spikes in hematocrit,
                    estrogen, and DHT. It then recommends the most
                    practical frequency to flatten it, set in one tap.
                </li>
                <li>
                    <strong>Responder Type (August 2026).</strong> The
                    first to put a blood test on a common basis,
                    correcting for draw timing, ester, and injection
                    frequency, then read it against published
                    dose-response trials to classify how efficiently a
                    man converts his dose into testosterone, from
                    super-responder to non-responder.
                    <DoiBadge slug="dose-response" />
                </li>
                <li>
                    <strong>Supplement Milestone Charting (August 2026).</strong>{" "}
                    The first app to chart a supplement against the
                    tissue pool that carries its effect, with the
                    milestone dated on the timeline: muscle saturation
                    for creatine, muscle carnosine for beta-alanine,
                    membrane stores for fish oil, 25-OH-D storage for
                    vitamin D.
                </li>
                <li>
                    <strong>Live Testosterone Readout (October 2026).</strong>{" "}
                    The first to estimate your Total T live, at any time on
                    your protocol, calibrated to your own blood test and
                    personalized dose response rather than a population
                    average.
                </li>
            </ul>

            <h2>Why it's built this way</h2>
            <p>
                TRT Plus is the output of one developer's decision that the
                optimization space deserved original thinking and real
                engineering. The community gets the benefit: ideas the
                category hadn't seen, built with pharmaceutical-grade
                precision and financial-grade engineering, documented and
                cited, in a consumer app.
            </p>
            <p>
                If you're a clinician, a coach, or someone who reads the
                methodology before trusting a number, the engine page is
                the place to start. Look under the hood.
            </p>

            <h2>Frequently asked questions</h2>

            <h3>Is the methodology actually documented?</h3>
            <p>
                Yes. The models, formulas, and cited studies are published
                on the <Link to="/engine">engine page</Link>, including the
                multi-route PK model, the A:E Androgenic Load formula, and
                the Cardio-Metabolic Cascade.
            </p>

            <h3>Can clinicians and coaches use it with clients?</h3>
            <p>
                TRT Plus is a consumer app, and its output is designed to
                inform the conversation with a healthcare provider, not
                replace it.
            </p>

            <h3>Who is the developer?</h3>
            <p>
                Nathan, founder and developer @ TRT Plus, designed, built,
                and maintains all of it: a career that began in
                pharmaceuticals and ran through two decades of
                financial-systems engineering.
            </p>

            <div className="hairline my-10" />

            <p className="text-white/50 text-sm italic">
                This article is for informational purposes only and does not
                constitute medical advice. Consult with your healthcare
                provider before making medical decisions.
            </p>

            <h3>Read the methodology</h3>
            <p>
                Every model, formula, and cited study behind these firsts
                is published on the engine page.{" "}
                <Link to="/engine">See the engine</Link> ·{" "}
                <Link to="/">Get TRT Plus</Link>.
            </p>

            <h3>Related reading</h3>
            <ul>
                <li>
                    <Link to="/blog/how-trt-plus-analyzes-your-bloodwork">
                        How TRT Plus Analyzes Your Bloodwork: A
                        Deterministic Engine, Then AI
                    </Link>
                </li>
                <li>
                    <Link to="/blog/tracking-a-bad-protocol-is-still-a-bad-protocol">
                        Tracking a Bad Protocol Is Still a Bad Protocol
                    </Link>
                </li>
            </ul>
        </>
    );
}
